/**
 * The campaign vocabulary: what each status is called, what a skipped recipient
 * is told, and how the send guard is described to somebody who is about to press
 * Schedule.
 *
 * It exists for the same reason `segment-display.ts` and `template-display.ts`
 * do — so the list, the create screen and the detail screen cannot describe the
 * same thing three different ways — and it keeps the same rule: **it invents no
 * number and no claim about a campaign.** The reach, the deliverable count, the
 * progress counts and the schedule issues are all answers the API gave on this
 * request; this file decides only the wording around them.
 *
 * Three things are imported rather than restated, and each one matters:
 *
 *   - **`CAMPAIGN_SKIP_REASONS`.** The sentence a skipped recipient carries
 *     lives in the contract, because the API writes the reason and this app
 *     renders it — a sentence maintained on only one side drifts into describing
 *     a rule the other side stopped following.
 *   - **`campaignReach()`.** The only supported way to turn a segment's counts
 *     into the number a campaign reaches. Slice 2 settled that showing the
 *     segment total where the reach belongs is *the* failure mode: it lets
 *     someone schedule an email campaign believing they are reaching 1,278
 *     people when 526 of them have an email address. Nothing here re-derives it.
 *   - **`campaignScheduleIssues()`**, via the readiness the API returns. The
 *     screen greys its button from that verdict and never computes its own.
 *
 * What this file adds is the half a stored value cannot carry: which of the
 * statuses this is at a glance, and — for the send guard — a calm sentence
 * saying that a campaign to 1,278 people delivering one message is the guard
 * working rather than the product being broken.
 */
import {
  CAMPAIGN_SKIP_REASONS,
  CAMPAIGN_SKIP_REASON_VALUES,
  DEFAULT_TIME_ZONE,
  HALTED_DELIVERY_RISK_MEANING,
  HOLDING_BETWEEN_WAVES_MEANING,
  isKnownTimeZone,
  type CampaignIssueSeverity,
  type CampaignReach,
  type CampaignScheduleIssue,
  type CampaignScheduleIssueCode,
  type CampaignSkipReason,
  type CampaignSkipReasonCounts,
  type CampaignStatus,
  type Channel,
  type MessageStatus,
  type RecipientStatus,
  type SendGuardStatus,
} from '@wend/shared';
import type { BadgeTone, CalloutTone } from '@wend/ui';

import { formatCount, formatDateTime, pluralise } from './format';

/* -------------------------------------------------------------------------- */
/* Channel                                                                     */
/* -------------------------------------------------------------------------- */

/** The channel, as a word. Lower case: it appears mid-sentence more than not. */
export const CHANNEL_LABEL: Readonly<Record<Channel, string>> = {
  email: 'email',
  whatsapp: 'WhatsApp',
};

/** What having an address on that channel means, for a reach caption. */
export const CHANNEL_ADDRESS_LABEL: Readonly<Record<Channel, string>> = {
  email: 'an email address',
  whatsapp: 'a phone number',
};

/**
 * **Who actually took the message**, by channel — the subject of nearly every
 * sentence a results screen writes.
 *
 * Until slice 12 this table did not need to exist, because only one provider
 * ever reported anything back: every "Amazon has not answered for these yet" on
 * the results panel was true by construction, since a WhatsApp campaign produced
 * no delivery feedback at all and the screens said so in one sentence and then
 * stopped. Slice 12 makes Meta report, and the moment it does, an unparameterised
 * "Amazon" becomes a claim about the wrong company on half the campaigns in the
 * product — an error that reads as harmless and is not, because a tenant chasing
 * a WhatsApp failure will go and look in the SES console.
 *
 * "Amazon SES" and "WhatsApp" rather than "SES" and "Meta": these strings are
 * read by a marketer, not by whoever wired the integration up. The tenant knows
 * they connected an Amazon account and they know they are sending WhatsApp
 * messages; "Meta" is the company and "WhatsApp" is the thing on the phone.
 */
export const CHANNEL_PROVIDER_LABEL: Readonly<Record<Channel, string>> = {
  email: 'Amazon SES',
  whatsapp: 'WhatsApp',
};

/**
 * The same, shortened for a second mention in the same paragraph.
 *
 * Repeating the full name four times in one sentence is how copy starts reading
 * like a contract, so the long form introduces and this one continues.
 */
export const CHANNEL_PROVIDER_SHORT_LABEL: Readonly<Record<Channel, string>> = {
  email: 'Amazon',
  whatsapp: 'WhatsApp',
};

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

export interface CampaignStatusDisplay {
  readonly label: string;
  readonly tone: BadgeTone;
  /** One line saying what the state means for whoever is looking at it. */
  readonly meaning: string;
}

/**
 * One entry per member of the fixed `CampaignStatus` enum.
 *
 * **`paused_insufficient_credits` is reachable, and its sentence used to deny
 * that it was.** It was written in slice 5, when credits did not exist, as
 * "credits arrive in a later slice, so nothing in this version can produce this
 * state" — a placeholder worded for the future, the same way
 * `TEMPLATE_STATUS_DISPLAY` words Meta's two states. Credits shipped in slice 6
 * and `campaign-send.runner.ts` has written this status ever since, so from that
 * slice on the screen asserted a campaign was in a state and denied in the next
 * clause that the state could exist. This is the failure class slice 17 was
 * called for and it survived four slices of readers, because nobody re-reads a
 * sentence that is already on the screen.
 *
 * What replaced it is what the runner actually does, and the second half is the
 * half a tenant needs: pausing leaves every unreached recipient `pending`, and
 * the next run resumes them — `startCampaign` accepts this status on the same
 * terms as `scheduled`. Nobody was skipped and nothing has to be rebuilt, which
 * is the opposite of what "stopped" reads as on its own.
 *
 * `sent` is the only green one, and it deliberately does not say "delivered".
 * Accepting a message is not delivering it; whether it reached an inbox is a
 * delivery event, and those are on the results panel.
 *
 * The known hole, named rather than hidden: `sending` says "Amazon SES" on a
 * WhatsApp campaign too. These are keyed on status alone, and every call site
 * has the campaign in hand, so the fix is the one `messageStatusMeaning` below
 * already took — a channel argument — rather than a second table.
 *
 * **Slice 18's two entries take their sentences from `@wend/shared` rather than
 * writing them here, and that is the same hole being routed around rather than
 * re-dug.** `halted_delivery_risk` is reachable on both channels and means
 * something different on each — bounces on email, unreachable numbers on
 * WhatsApp — so the status-keyed sentence deliberately says neither. It says
 * what is true of both: sending stopped part-way because of how the messages
 * already sent were received. The channel-specific half lives in
 * `CAMPAIGN_HALT_SENTENCES`, keyed on `campaigns.halt_reason`, which is a
 * per-channel vocabulary; the campaign screen renders that beneath this.
 *
 * The two are also deliberately not interchangeable in wording.
 * `holding_between_waves` continues on its own and `halted_delivery_risk` waits
 * for a person, and those sentences are the only place a reader learns which of
 * those two things is happening to their campaign.
 */
export const CAMPAIGN_STATUS_DISPLAY: Readonly<Record<CampaignStatus, CampaignStatusDisplay>> = {
  draft: {
    label: 'Draft',
    tone: 'neutral',
    meaning:
      'Nothing has been built and nothing can be sent. A draft has no recipient list — that is created when you schedule it.',
  },
  scheduled: {
    label: 'Scheduled',
    tone: 'info',
    meaning:
      'The recipient list has been built and is waiting for its time. Cancel it if you need to change anything.',
  },
  sending: {
    label: 'Sending',
    tone: 'info',
    /*
     * THE ONE ENTRY THAT NAMES A PROVIDER, so it is the one entry a channel can
     * make false — `campaignSendingMeaning` below is what screens call. Slice 17
     * recorded this as a known wart and slice 24 is where it stops being one: a
     * WhatsApp campaign was told its messages were going to Amazon SES.
     *
     * The map keeps a channel-free version rather than losing one, because
     * `CAMPAIGN_STATUS_DISPLAY` is keyed on status alone and several readers have
     * no channel in hand — a status filter on the campaign list, for one.
     */
    meaning: 'Messages are being handed to the sending provider now, one recipient at a time.',
  },
  paused_insufficient_credits: {
    label: 'Out of credits',
    tone: 'warn',
    meaning:
      'Sending stopped part-way because the workspace ran out of credit. Everybody already reached keeps their result; everybody not yet reached is still waiting, not skipped, and sending resumes on its own once credit is added.',
  },
  sent: {
    label: 'Sent',
    tone: 'good',
    meaning:
      'Every recipient has been resolved: sent to, or skipped with a reason you can read below.',
  },
  failed: {
    label: 'Failed',
    tone: 'bad',
    meaning: 'The send stopped on an error before every recipient had been resolved.',
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    meaning:
      'Stopped by hand. Anyone still waiting was skipped rather than left pending, so these numbers are final.',
  },
  holding_between_waves: {
    label: 'Waiting between batches',
    // `info`, not `warn`. Holding is the plan working, and painting the ordinary
    // course of a large send in a warning colour teaches people to ignore the
    // colour — the argument `RECIPIENT_STATUS_DISPLAY.skipped` already makes.
    tone: 'info',
    meaning: HOLDING_BETWEEN_WAVES_MEANING,
  },
  halted_delivery_risk: {
    label: 'Stopped — needs a look',
    // `warn`, not `bad`. Nothing has failed and nothing is lost: everyone not
    // yet reached is still waiting. `bad` is for `failed`, where the send ended
    // on an error, and the two must not read as the same outcome.
    tone: 'warn',
    meaning: HALTED_DELIVERY_RISK_MEANING,
  },
  // Slice 68: a flow's step campaign. Connect lists exclude these, so this is
  // read only where a step's campaign is shown from a flow.
  flow: {
    label: 'Flow step',
    tone: 'info',
    meaning:
      'Part of a flow: people receive this message one at a time, as they reach this step. It is sent and measured from the flow, not scheduled here.',
  },
};

/* -------------------------------------------------------------------------- */
/* Recipient and message status                                                */
/* -------------------------------------------------------------------------- */

export interface RecipientStatusDisplay {
  readonly label: string;
  readonly tone: BadgeTone;
}

/**
 * One entry per member of `RecipientStatus`.
 *
 * `sent` is `info` and `delivered` is `good`, and the distinction is the point:
 * Amazon accepting a message is not the same as somebody receiving it. Slice 5
 * can only ever produce `sent`; `delivered` and `bounced` are written by the
 * delivery webhooks in slice 6.
 *
 * `skipped` is `neutral` rather than a warning. A skipped recipient is usually
 * the send guard doing its job, and painting the ordinary outcome of a guarded
 * environment in a warning colour teaches people to ignore the colour.
 */
export const RECIPIENT_STATUS_DISPLAY: Readonly<Record<RecipientStatus, RecipientStatusDisplay>> = {
  pending: { label: 'Waiting', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'info' },
  delivered: { label: 'Delivered', tone: 'good' },
  bounced: { label: 'Bounced', tone: 'bad' },
  failed: { label: 'Failed', tone: 'bad' },
  skipped: { label: 'Skipped', tone: 'neutral' },
};

/**
 * What the *message* status says, which is a different question from the
 * recipient's: the recipient's status is about this person, the message's is
 * about the provider.
 *
 * **One table per channel, and the split is not translation.** This was a single
 * `Record<MessageStatus, string>` full of the word "Amazon" from slice 5 until
 * slice 12, and it was defensible for exactly as long as a WhatsApp message
 * could never leave `sent`: the two rows a reader could reach on that channel
 * were wrong about the provider's *name* and right about everything else. Slice
 * 12's status ingest makes `delivered` and `failed` reachable on WhatsApp, and
 * at that point the same string stops being a naming slip and becomes a false
 * statement of fact — "Amazon reported it delivered" on a message Amazon never
 * touched sends somebody to the wrong console to debug it.
 *
 * The two columns then turn out to disagree about more than the name, which is
 * the reason this is a table rather than a template with the provider
 * substituted in:
 *
 *   - **`failed` means opposite things.** On email it is a *pre-acceptance*
 *     refusal: SES rejected the message and it never entered the mail system, so
 *     nothing is in flight. On WhatsApp it is a *post-acceptance* report: the
 *     Graph API took the message and gave back an id, and Meta said afterwards
 *     that it did not arrive. Describing the second as "refused before it left"
 *     would have a tenant looking for a rejection that never happened;
 *   - **`bounced` is unreachable on WhatsApp and stays in the table anyway.**
 *     Decision 9 of this slice: the WhatsApp ingest writes `failed`, never
 *     `bounced`, because a `bounced` row carrying no SES `bounceType` files
 *     itself under `undetermined` in front of `bounceBreakdown` — a wrong number
 *     that looks like a measurement. `MessageStatus` is one enum over both
 *     channels, so the compiler wants a sentence here regardless; what it gets
 *     is one that says the state should not exist, so that a reader who somehow
 *     sees it is looking at a bug report rather than at an explanation.
 *
 * `unknown` is the one worth wording carefully on both channels. It means the
 * provider accepted the message and the worker died before recording it —
 * deliberately parked rather than retried, because a retry there cannot tell
 * "never sent" from "sent, unrecorded", and guessing wrong sends somebody a
 * second copy.
 */
const MESSAGE_STATUS_MEANING_BY_CHANNEL: Readonly<
  Record<Channel, Readonly<Record<MessageStatus, string>>>
> = {
  email: {
    queued: 'Handed to the send loop, not yet accepted by Amazon.',
    sent: 'Amazon SES accepted this message.',
    delivered: 'Amazon reported it delivered.',
    bounced: 'It bounced. The address will be suppressed.',
    failed: 'Amazon refused it. Nothing was delivered.',
    unknown:
      'Amazon accepted this message but the record of it was never written. It is left alone rather than retried, because a retry cannot tell “never sent” from “sent, unrecorded”.',
  },
  whatsapp: {
    queued: 'Handed to the send loop, not yet accepted by WhatsApp.',
    sent: 'WhatsApp accepted this message. It has not said whether the phone received it.',
    delivered: 'WhatsApp reported it delivered to the phone.',
    bounced:
      'WhatsApp has no such thing as a bounce, so this state should not exist on this campaign. Please report it.',
    failed:
      'WhatsApp took this message and then reported that it did not arrive. The reason is in the failure breakdown on the results panel.',
    unknown:
      'WhatsApp accepted this message but the record of it was never written. It is left alone rather than retried, because a retry cannot tell “never sent” from “sent, unrecorded”.',
  },
};

/**
 * What one message's status means, on the channel it was sent on.
 *
 * A function rather than the exported table it used to be, so that every call
 * site has to produce a channel to get a sentence at all. The two call sites —
 * the recipient table and the results panel — both have the campaign in hand,
 * so this costs an argument and buys the guarantee that neither can print the
 * wrong provider's vocabulary by forgetting which channel it is drawing.
 */
export function messageStatusMeaning(status: MessageStatus, channel: Channel): string {
  return MESSAGE_STATUS_MEANING_BY_CHANNEL[channel][status];
}

/* -------------------------------------------------------------------------- */
/* Skip reasons — where a person finds out why somebody got nothing            */
/* -------------------------------------------------------------------------- */

/**
 * The tone of a skip reason's chip.
 *
 * All but one are `neutral`, and `not_on_send_allowlist` is `info` rather than
 * `warn`. It is the reason nearly every recipient carries in a guarded
 * environment, and it is a configured outcome rather than a fault — painting it
 * amber would make the correct behaviour of the send guard look like damage
 * every single time.
 */
export const SKIP_REASON_TONE: Readonly<Record<CampaignSkipReason, BadgeTone>> = {
  not_on_send_allowlist: 'info',
  suppressed: 'neutral',
  no_email_address: 'neutral',
  // Slice 24, item 11: the WhatsApp half of the same fact, and the same tone —
  // a contact without the channel's address is a list fact, not a fault.
  no_phone_number: 'neutral',
  duplicate_in_segment: 'neutral',
  campaign_cancelled: 'neutral',
  // Slice 59: a choice somebody made at the schedule step, not a fault — and
  // 'warn' would read as the campaign having done something wrong to them.
  rested: 'neutral',
  // Slice 61: likewise — a share held back on purpose, to measure the send.
  holdout: 'neutral',
};

/**
 * Display order for the breakdown.
 *
 * The send guard first, deliberately: SLICES says the reason is usually the send
 * guard, and the first line of a breakdown is the one people read. The rest run
 * from "something you could change" to "nothing to do about it".
 */
export const SKIP_REASON_ORDER: readonly CampaignSkipReason[] = [
  'not_on_send_allowlist',
  'suppressed',
  // Slice 59: a choice the sender made, like an opt-out is the recipient's.
  // Missing from this list, a campaign that rested 7 said "Why 30 received
  // nothing" over rows adding to 23 — found in review.
  'rested',
  // Slice 61: held back to measure the campaign — the sender's choice too.
  'holdout',
  'campaign_cancelled',
  'duplicate_in_segment',
  'no_email_address',
  // Missing since slice 24 for the same reason, and found by the same review:
  // a WhatsApp campaign's "no phone number" rows were dropped from the sum.
  'no_phone_number',
];

export interface SkipReasonRow {
  readonly reason: CampaignSkipReason;
  readonly count: number;
  /** Two or three words, for a chip. From the contract. */
  readonly label: string;
  /** A whole sentence a customer can act on. From the contract. */
  readonly sentence: string;
  readonly tone: BadgeTone;
}

/**
 * The breakdown, as rows a screen can render — **never one "skipped" total.**
 *
 * "1,278 skipped" is not an explanation; it is the shape of one. Every reason
 * with somebody in it gets its own line, its own count and the contract's own
 * sentence, so the answer to "why did nobody get this" is on the screen rather
 * than in a support conversation.
 *
 * Reasons with nobody in them are dropped. A zero beside "Unsubscribed" on a
 * campaign where nobody unsubscribed is noise, and the sum of what is left still
 * equals `counts.skipped` — the API only ever writes a member of the closed set.
 */
export function skipReasonRows(counts: CampaignSkipReasonCounts): SkipReasonRow[] {
  return SKIP_REASON_ORDER.filter((reason) => counts[reason] > 0).map((reason) => ({
    reason,
    count: counts[reason],
    label: CAMPAIGN_SKIP_REASONS[reason].label,
    sentence: CAMPAIGN_SKIP_REASONS[reason].sentence,
    tone: SKIP_REASON_TONE[reason],
  }));
}

/** Every reason, for a filter control — including the ones nobody is in. */
export const ALL_SKIP_REASONS: readonly CampaignSkipReason[] = CAMPAIGN_SKIP_REASON_VALUES;

/* -------------------------------------------------------------------------- */
/* Schedule issues                                                             */
/* -------------------------------------------------------------------------- */

const SEVERITY_TONE: Readonly<Record<CampaignIssueSeverity, CalloutTone>> = {
  blocker: 'bad',
  advice: 'warn',
};

/**
 * The tone for the four pieces of advice, where the severity alone is too blunt.
 *
 * The two send-guard members are `info`, not `warn`. They are the most common
 * thing this screen will ever say — in a development environment every campaign
 * carries one — and they describe the guard working exactly as designed. An
 * amber triangle on the expected outcome is how a person learns to scroll past
 * the warnings that are not expected, such as the sandbox one directly above it.
 */
const ISSUE_TONE: Partial<Readonly<Record<CampaignScheduleIssueCode, CalloutTone>>> = {
  send_allowlist_empty: 'info',
  send_guard_restricts_delivery: 'info',
};

/**
 * What `sending` means, in the words of the channel it is actually using.
 *
 * `CAMPAIGN_STATUS_DISPLAY` is keyed on status alone, which is right for a badge
 * and wrong for the one sentence that names a provider: a WhatsApp campaign was
 * told its messages were "handed to Amazon SES now", which is a sentence about
 * somebody else's infrastructure. Slice 17 recorded it; this is the repair.
 *
 * A function rather than a second map keyed on `${status}:${channel}`, because
 * exactly one status differs by channel and a doubled map would invite the other
 * eleven to drift. Every other status returns the shared sentence unchanged.
 *
 * The provider is named rather than generalised — "Amazon SES", "Meta" — because
 * the reader who needs this sentence is the one about to go and look at a
 * provider's dashboard, and "the sending provider" tells them nothing about
 * which one.
 */
export function campaignSendingMeaning(status: CampaignStatus, channel: Channel): string {
  const display = CAMPAIGN_STATUS_DISPLAY[status];
  if (status !== 'sending') return display.meaning;
  return channel === 'whatsapp'
    ? 'Messages are being handed to Meta now, one recipient at a time.'
    : 'Messages are being handed to Amazon SES now, one recipient at a time.';
}

export function issueTone(issue: CampaignScheduleIssue): CalloutTone {
  return ISSUE_TONE[issue.code] ?? SEVERITY_TONE[issue.severity];
}

/**
 * The heading above an issue's own message.
 *
 * **Headings, not repairs** — the same rule `template-display.ts` follows. The
 * repair is the sentence `campaignScheduleIssues` produced, which the API sends
 * and the screen prints unchanged; restating it here would give a person two
 * accounts of the same problem that can disagree. What this adds is which of the
 * twelve it is, at a glance, above the paragraph that says what to do.
 */
export const CAMPAIGN_ISSUE_TITLE: Readonly<Record<CampaignScheduleIssueCode, string>> = {
  campaign_not_draft: 'This campaign is not a draft',
  template_not_approved: 'The template is not approved',
  template_channel_mismatch: 'The template is for a different channel',
  no_reachable_recipients: 'Nobody in this segment can be reached',
  /*
   * It said "An image in this template has nowhere to be served from", which
   * described a deployment with no image host. Since hosting went behind
   * configuration this issue only exists where a host IS configured — the fault
   * is a picture not yet published to it, fixed by saving the template — and
   * "nowhere to be served from" sent the reader looking for missing
   * infrastructure. It now uses the editor's word for the same state.
   */
  template_images_not_hostable: 'A picture in this template is not published yet',
  // Slice 23's file version of the same state, in the same words for the same
  // reason. "File" and not "attachment": on a deployment that links them there
  // is no attachment in the message to speak of.
  template_attachments_not_linkable: 'A file in this template is not published yet',
  integration_not_ready: 'The sending account cannot send',
  from_address_not_verified: 'The from address is not verified with AWS',
  scheduled_in_past: 'That time has already passed',
  quiet_hours: 'This lands in the middle of the night',
  send_allowlist_empty: 'The send allow-list is empty',
  send_guard_restricts_delivery: 'The send guard will hold most of this back',
  sending_account_sandboxed: 'This AWS account is still in the SES sandbox',

  /*
   * SLICE 49 — THE WHATSAPP SIX, AND THE THREE ABOVE THEY MUST NEVER STAND IN FOR.
   *
   * `integration_not_ready`, `from_address_not_verified` and
   * `sending_account_sandboxed` are all about an Amazon SES account, and a
   * WhatsApp campaign has none: `campaignScheduleIssues` reaches the SES
   * members only inside its `channel === 'email'` branch, so none of the three
   * can arrive on a WhatsApp campaign and none of these six can arrive on an
   * email one. They are headings for a different account, not translations of
   * the same one.
   *
   * `whatsapp_account_health` is one code carrying five different advisories —
   * never checked, a RED quality rating, a WABA still under review, incomplete
   * business verification, a token about to expire. The heading therefore says
   * only *which account* it is about; the sentence underneath is the server's,
   * and it is the one that says which of the five happened. A heading that
   * guessed would be right a fifth of the time.
   *
   * THE BINDINGS PAIR SAYS NO NUMBER, AND USES THE PANEL'S OWN WORD.
   *
   * `ScheduleCampaignPanel` draws the heading directly above the server's
   * sentence, and both server sentences branch on how many positions they are
   * about — "…has no field for it" against "…has no field for them". A
   * singular heading over the plural sentence read "A position in this
   * template has no field" above "…needs {{1}} and {{3}}… no field for them",
   * which is a heading disagreeing with its own paragraph about a count, on
   * the screen whose whole job is counts. So neither heading commits to one or
   * many; the sentence beneath is the only thing here that knows.
   *
   * They also say "filled" rather than "field". "Field" is Wend's internal
   * word for a binding's source and it appears nowhere a reader can see: the
   * control on the personalisation panel is labelled **Filled by**, and a
   * refusal that names a control by a second name is a refusal nobody can act
   * on.
   */
  wa_bindings_incomplete: 'Not every position in this template is filled',
  wa_bindings_unused: 'Not everything filled for this campaign is still used',
  whatsapp_account_not_connected: 'No WhatsApp account is connected',
  whatsapp_number_not_verified: 'Meta has not verified this number',
  whatsapp_token_expired: 'This WhatsApp token has expired',
  whatsapp_account_health: 'About your WhatsApp account at Meta',
};

/** Blockers first: they are what the button is disabled for. */
export function sortIssues(issues: readonly CampaignScheduleIssue[]): CampaignScheduleIssue[] {
  return [...issues].sort((a, b) => {
    if (a.severity === b.severity) return 0;
    return a.severity === 'blocker' ? -1 : 1;
  });
}

/* -------------------------------------------------------------------------- */
/* The send guard                                                              */
/* -------------------------------------------------------------------------- */

/**
 * What the create screen says about the guard, before any campaign exists.
 *
 * It can be said without a single number because it is unconditionally true:
 * `sendGuardStatusSchema.enforced` is `z.literal(true)`, so **the contract has
 * no way to express a guard that is off**. Turning it off is a contract change
 * with a review, not a config flip, and this app cannot do it at all.
 */
export const SEND_GUARD_ALWAYS_ON_TITLE = 'Sending is restricted to an allow-list';

export const SEND_GUARD_ALWAYS_ON_BODY =
  'Wend will not deliver to an address that is not on this environment’s send allow-list. Everyone else becomes a skipped recipient carrying the reason, listed on the campaign — nothing is quietly dropped. That is what stops a development environment emailing a thousand real organisations, and it cannot be switched off from this app. Exactly how many of these people will receive the message is counted on the campaign itself, before you schedule it.';

export interface SendGuardReading {
  /**
   * How many messages will actually be delivered, or `null` when it has not been
   * computed. **The honest number**, and the one that belongs beside the reach.
   */
  readonly deliverable: number | null;
  /** The channel reach the guard is cutting down. Never the segment size. */
  readonly reachable: number;
  /** Reachable people the guard will hold back. `null` when unknown. */
  readonly withheld: number | null;
  /** Nothing at all will be delivered — an empty list, or nobody on it. */
  readonly blocksEverything: boolean;
  /** Some will be delivered and some will not. */
  readonly restricts: boolean;
  /** Everyone this campaign reaches is allow-listed. */
  readonly unrestricted: boolean;
  /** One line, in bold, above the explanation. */
  readonly headline: string;
  /** The paragraph under it. Calm, and it never calls the guard a fault. */
  readonly explanation: string;
  readonly tone: CalloutTone;
}

/**
 * What the guard will do to *this* campaign, in words.
 *
 * The whole point of the wording: **a campaign to 1,278 people that delivers one
 * message is correct**, and a person who schedules it and then reads "1 sent,
 * 1,277 skipped" without having been told will conclude the product is broken.
 * So the number is said before the button is pressed, in the same panel as the
 * button, and the sentence says the guard is doing its job.
 *
 * Every figure comes from the API: `allowlistSize` and `deliverable` are the
 * guard's own, `reachable` is `campaignReach`'s. Nothing is estimated, and the
 * allow-listed addresses themselves are deliberately not on the wire — a count
 * answers the question the screen is asking, and shipping the operator's test
 * addresses to a browser answers one nobody asked.
 */
export function readSendGuard(guard: SendGuardStatus, reach: CampaignReach): SendGuardReading {
  const reachable = reach.reachable;
  const deliverable = guard.deliverable;
  const withheld = deliverable === null ? null : Math.max(0, reachable - deliverable);
  const channel = CHANNEL_LABEL[reach.channel];

  if (guard.allowlistSize === 0) {
    return {
      deliverable,
      reachable,
      withheld,
      blocksEverything: true,
      restricts: false,
      unrestricted: false,
      tone: 'info',
      headline: 'Nothing will be delivered: the send allow-list is empty',
      explanation: `Every one of the ${pluralise(reachable, 'person', 'people')} this campaign reaches by ${channel} will be skipped, each carrying the reason, and no message will be handed to Amazon. That is the guard failing closed rather than a fault in this campaign — an empty allow-list is the safest state a sending system can be in, and it is the ordinary state of a fresh environment. The campaign will still schedule, materialise and finish, which is how you can see exactly who would have been written to.`,
    };
  }

  if (deliverable === null) {
    return {
      deliverable,
      reachable,
      withheld,
      blocksEverything: false,
      restricts: false,
      unrestricted: false,
      tone: 'info',
      headline: `Delivery is restricted to ${pluralise(guard.allowlistSize, 'allow-listed address', 'allow-listed addresses')}`,
      explanation:
        'How many of this campaign’s recipients are on that list is counted on the campaign itself. Anybody who is not becomes a skipped recipient carrying the reason — nothing is delivered to an address the allow-list does not name.',
    };
  }

  if (deliverable === 0) {
    return {
      deliverable,
      reachable,
      withheld,
      blocksEverything: true,
      restricts: false,
      unrestricted: false,
      tone: 'info',
      headline: 'Nothing will be delivered: nobody here is on the send allow-list',
      explanation: `The allow-list holds ${pluralise(guard.allowlistSize, 'address', 'addresses')} and none of them is in this segment, so all ${formatCount(reachable)} reachable ${reachable === 1 ? 'person' : 'people'} will be skipped with a reason. Add one of these addresses to SEND_ALLOWLIST — or pick a segment containing an address that is already on it — and that recipient will receive the message.`,
    };
  }

  if (deliverable < reachable) {
    return {
      deliverable,
      reachable,
      withheld,
      blocksEverything: false,
      restricts: true,
      unrestricted: false,
      tone: 'info',
      headline: `${formatCount(deliverable)} of ${formatCount(reachable)} will actually receive this`,
      explanation: `The other ${pluralise(withheld ?? 0, 'person', 'people')} are not on this environment’s send allow-list, so nothing is sent to them: they become skipped recipients, each carrying the reason, and you can read the whole list on the campaign afterwards. That is expected here and it is not the product failing — it is the guard that stops a development environment writing to real contacts.`,
    };
  }

  return {
    deliverable,
    reachable,
    withheld,
    blocksEverything: false,
    restricts: false,
    unrestricted: true,
    tone: 'good',
    headline: `All ${formatCount(reachable)} ${reachable === 1 ? 'recipient is' : 'recipients are'} on the send allow-list`,
    explanation: `Every address this campaign reaches by ${channel} is allow-listed, so the guard will hold nothing back. It still runs — once when the recipient list is built and once more immediately before each message is handed to Amazon, because a list built today can be sent tomorrow under a different allow-list.`,
  };
}

/* -------------------------------------------------------------------------- */
/* Reach                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The reach, in words — the caption under the headline number.
 *
 * The rule this wording exists to keep: `reachable` is a **ceiling, not a
 * promise**. The suppression list, duplicate addresses and the send guard all
 * cut it further, and presenting it as "messages that will be delivered" is the
 * same over-promise slice 2 built the three-count shape to prevent, one level
 * down. So the sentence says "can be reached", and the guard's own number is
 * always shown next to it.
 */
export function describeReach(reach: CampaignReach): string {
  const channel = CHANNEL_LABEL[reach.channel];

  if (reach.segmentUniqueRecipients === 0) {
    return 'This segment has nobody in it yet, so there is nobody to reach.';
  }

  if (reach.unreachable === 0) {
    return `Every one of the ${pluralise(reach.segmentUniqueRecipients, 'person', 'people')} in this segment has ${CHANNEL_ADDRESS_LABEL[reach.channel]}.`;
  }

  return `This segment holds ${pluralise(reach.segmentUniqueRecipients, 'person', 'people')}, and ${formatCount(reach.unreachable)} of them ${reach.unreachable === 1 ? 'has' : 'have'} no ${reach.channel === 'email' ? 'email address' : 'phone number'} — a ${channel} campaign cannot touch them. Wend never falls back to the other channel to make this number bigger.`;
}

/* -------------------------------------------------------------------------- */
/* Time                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The timezone this browser is in, or the launch market's when it cannot say.
 *
 * A campaign stores the zone as well as the instant, and the zone is what the
 * screen renders and what the quiet-hours warning is judged in. The wall-clock
 * time somebody types into a `datetime-local` input is interpreted by the
 * platform in **this** zone, so this is the only zone the campaign can honestly
 * claim the time was chosen in.
 *
 * `resolved: false` means `Intl` could not name the zone. The instant is still
 * correct — the platform parsed the wall clock in its own local time either way
 * — but the *name* stored alongside it is then a guess, so the screen says which
 * zone it is showing rather than leaving it implied.
 *
 * **Slice 13 gave it a second caller with a heavier consequence**, and it is
 * reused rather than re-implemented there so that this app has one answer to
 * "which zone is this browser in". A report window is stored with the zone it
 * was chosen in and that zone is printed on the sheet, so a `resolved: false`
 * there is not only a label to hedge: the boundaries the reports screen prints
 * are formatted in the named zone, which is how an unaligned window shows itself
 * as boundaries that are not midnight rather than passing silently.
 */
export function browserTimeZone(): { readonly timezone: string; readonly resolved: boolean } {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (resolved !== '' && isKnownTimeZone(resolved)) return { timezone: resolved, resolved: true };
  } catch {
    // Falls through to the default. An ICU-less runtime is not a reason to
    // refuse to schedule anything.
  }
  return { timezone: DEFAULT_TIME_ZONE, resolved: false };
}

/** `2026-09-05T09:00` — what a `datetime-local` input reads and writes. */
export function toDateTimeLocalValue(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/**
 * The instant a `datetime-local` value names, or `null` if it names none.
 *
 * `new Date('2026-09-05T09:00')` is parsed as **local** time by every runtime
 * that has shipped this century, which is what makes the wall clock somebody
 * typed and the zone stored on the campaign describe the same moment.
 */
export function fromDateTimeLocalValue(value: string): Date | null {
  if (value === '') return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : new Date(time);
}

/**
 * An instant, rendered in the campaign's own timezone rather than the reader's.
 *
 * Which is the whole reason the zone is stored: "did they mean 9am in Mumbai?"
 * is the question a rescheduling customer is actually asking, and answering it
 * in the reader's timezone answers a different one. Falls back to the reader's
 * locale formatting if the platform will not accept the zone, because a
 * scheduled time that renders as an em dash is worse than one in the wrong zone.
 */
export function formatInTimeZone(iso: string, timezone: string): string {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return formatDateTime(iso);

  try {
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    }).format(new Date(time));
  } catch {
    return formatDateTime(iso);
  }
}

/** The scheduled instant with the zone it was chosen in named after it. */
export function scheduleTimeLabel(iso: string, timezone: string): string {
  return `${formatInTimeZone(iso, timezone)} (${timezone})`;
}

/* -------------------------------------------------------------------------- */
/* The list's sent meter                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The share a finished campaign's list meter draws: sent ÷ (sent + skipped),
 * or `null` for no meter.
 *
 * The first slice 66 build divided `sentCount` by `reach.reachable`. That is a
 * ratio of two different things: `reachable` is the SEGMENT's reach as it
 * stands today, recomputed on every read — the contract calls it "a ceiling,
 * not a promise" — and not this campaign's recipient count. A campaign sent
 * under the re-closed send guard drew a nearly empty bar beside "Sent",
 * reading as a failed send; a segment that grew after the send made an old
 * campaign look unfinished. The page invented a shortfall on the screen the
 * owner uses to judge real sends.
 *
 * Both halves here are the campaign's own resolved recipients, already on the
 * summary and printed beside the bar. It is drawn only for a `sent` campaign
 * — while one is sending or held, sent + skipped is not yet its whole list —
 * and only when that total is above zero, because a share of nothing is no
 * bar, not a bar at 0%.
 */
export function sentShare(campaign: {
  readonly status: CampaignStatus;
  readonly sentCount: number;
  readonly skippedCount: number;
}): number | null {
  if (campaign.status !== 'sent') return null;
  const resolved = campaign.sentCount + campaign.skippedCount;
  return resolved > 0 ? campaign.sentCount / resolved : null;
}
